import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import connect from '../../utils/connect';
import { Button, Row, Col } from '@app/ui';
import { getRightsholderTextTrackingFilters } from '../../actions/home/getRightsholderTextTrackingFilters';
import TextTrackingModal from './TextTrackingModal';
import * as R from 'ramda';
import { updateRightsholderTextTrackingFilter } from '../../actions/settings/updateRightsholderTextTrackingFilter';
import AccordionContainer from '@app/ui/sports-components/AccordionContainer';
import AccordionList from '@app/ui/sports-components/AccordionList';
import RhTextTrackingConfiguration from './RhTextTrackingConfiguration';
import './TextTracking.scss';

const TextTracking = ({ client, filters }) => {
    const { t } = useTranslation('settings');
    const [isLoading, setIsLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [rightsholders, setRightsholders] = useState([]);
    const [rightsholdersWithData, setRightsholdersWithData] = useState([]);
    const closeModal = useCallback(() => setIsModalOpen(false), []);
    const openModal = useCallback(() => setIsModalOpen(true), []);

    const formatTableData = (
        rightsholderTextTrackingFilters,
        accountsByRhId,
    ) => {
        return rightsholderTextTrackingFilters
            .map(el => el.rightsholderId)
            .filter((el, idx, arr) => {
                // get all unique rhIds in rightsholderTextTrackingFilters
                return arr.indexOf(el) === idx;
            })
            .sort()
            .map(rhId => {
                const data = rightsholderTextTrackingFilters.filter(
                    ttf => ttf.rightsholderId === rhId,
                );

                const first = data[0];
                return {
                    accounts: accountsByRhId[first.rightsholderId],
                    rightsholderId: first.rightsholderId,
                    rightsholderName: first.rightsholderName,
                    rhTextTrackingFilters: data,
                };
            });
    };

    useEffect(() => {
        if (filters.isLoading) return;

        rightsholdersWithData.forEach(rhTTFData => {
            if (!rhTTFData.rightsholderId) return;
            const rh = filters.rightsholders.list[rhTTFData.rightsholderId];
            if (!rh || !rh.mainLogo || !rh.mainLogo.url) return;

            rhTTFData.rhLogo = rh.mainLogo.url;
        });

        setRightsholdersWithData(R.clone(rightsholdersWithData));
    }, [rightsholders, filters.isLoading]);

    const updateTextTrackingFilters = useCallback(() => {
        (async () => {
            try {
                setIsLoading(true);
                const { rightsholderTextTrackingFilters, rightsholders } =
                    await getRightsholderTextTrackingFilters({
                        clientId: client.id,
                    });

                const normalizedRightsholders = Array.isArray(rightsholders)
                    ? rightsholders
                    : [];
                const normalizedRhTextTrackingFilters = Array.isArray(
                    rightsholderTextTrackingFilters,
                )
                    ? rightsholderTextTrackingFilters
                    : [];

                const accountsByRhId = R.mergeAll(
                    normalizedRightsholders.map(r => ({
                        [r.id]: Array.isArray(r.accounts) ? r.accounts : [],
                    })),
                );

                const formatted = formatTableData(
                    normalizedRhTextTrackingFilters,
                    accountsByRhId,
                );
                setRightsholdersWithData(formatted);

                setRightsholders(normalizedRightsholders);
            } finally {
                setIsLoading(false);
            }
        })();
    }, [client]);

    const onChangeStatus = async (
        rightsholderTextTrackingFilterId,
        shouldActivate,
    ) => {
        await updateRightsholderTextTrackingFilter(
            client.id,
            rightsholderTextTrackingFilterId,
            shouldActivate,
        ).res(async () => {
            const {
                rightsholderTextTrackingFilters: updatedRhTextTrackingFilters,
            } = await getRightsholderTextTrackingFilters({
                clientId: client.id,
            });
            const normalizedUpdatedRhTextTrackingFilters = Array.isArray(
                updatedRhTextTrackingFilters,
            )
                ? updatedRhTextTrackingFilters
                : [];

            // statuses by Rh-TTF Id
            const statusById = R.mergeAll(
                normalizedUpdatedRhTextTrackingFilters.map(r => ({
                    [r.rightsholderTextTrackingFilterId]: r.active,
                })),
            );

            rightsholdersWithData.forEach(rhTTFData => {
                rhTTFData.rhTextTrackingFilters.forEach(
                    t =>
                        (t.active =
                            statusById[t.rightsholderTextTrackingFilterId]),
                );
            });

            setRightsholdersWithData(R.clone(rightsholdersWithData));
        });
    };

    useEffect(() => updateTextTrackingFilters(), [client]);

    return (
        <>
            {isModalOpen && (
                <TextTrackingModal
                    rightsholders={rightsholders}
                    onClose={closeModal}
                    updateTextTrackingFilters={updateTextTrackingFilters}
                />
            )}
            <Row gutter={16}>
                <Col md={20}>
                    <i className="fas fa-info-circle -m-h-1 gds-text--info -m-r-2" />
                    <span className="gds-text--body-sm">
                        {t('clientConfiguration.textTracking.info')}
                    </span>
                </Col>
                <Col md={4} className="-m-b-2">
                    <Button
                        block
                        size="small"
                        variant="primary"
                        onClick={openModal}
                    >
                        {t('clientConfiguration.textTracking.addTrackedText')}
                    </Button>
                </Col>
            </Row>
            <Row gutter={16}>
                <Col span={24}>
                    <div className="text-tracking-filters-accordion">
                        <AccordionContainer>
                            <AccordionList>
                                {rightsholdersWithData.map(
                                    (rhTtfData, _, data) => (
                                        <RhTextTrackingConfiguration
                                            key={rhTtfData.rightsholderId}
                                            data={rhTtfData}
                                            isLoading={isLoading}
                                            onChangeStatus={onChangeStatus}
                                            shouldExpand={data?.length === 1}
                                        />
                                    ),
                                )}
                            </AccordionList>
                        </AccordionContainer>
                    </div>
                </Col>
            </Row>
        </>
    );
};

TextTracking.propTypes = {
    client: PropTypes.object.isRequired,
    filters: PropTypes.object.isRequired,
};

export default connect(['client', 'filters'])(TextTracking);
